import { Button, Checkbox, Modal, Upload } from "antd";
import {
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { errorMessage } from "../../utils/notificationUtils";

export type BillRequiredItem = {
  itemDate: string;
  itemName: string;
  itemCategory?: string;
};

export type BillItemChecklistEntry = {
  itemName: string;
  itemDate?: string;
  checked: boolean;
};

export type ClaimExtraDocument = {
  documentId: number;
  documentName: string;
  documentType: string;
  documentUrl: string;
  documentLabelType: string;
  id?: number;
  itemNames: BillItemChecklistEntry[];
};

export const mapClaimExtraDocumentsToDisplayUrls = (
  claimExtraDocuments: ClaimExtraDocument[] = [],
) =>
  claimExtraDocuments
    ?.filter((entry) => entry?.documentUrl)
    ?.map((entry) => ({
      id: entry.id,
      documentType: entry.documentType,
      documentName: entry.documentName,
      documentUrl: entry.documentUrl,
      documentLabelType: entry.documentLabelType || entry.documentName,
      isBillRequiredDocument: true,
    })) || [];

type BillRequiredDocumentModalProps = {
  visible: boolean;
  documentName: string;
  itemNames: string[];
  itemEntries?: BillRequiredItem[];
  uploadedDocument?: ClaimExtraDocument | null;
  initialCheckedItems?: BillItemChecklistEntry[];
  hideItemChecklist?: boolean;
  portalSubmission: boolean;
  isUploading: boolean;
  onCancel: () => void;
  onUpload: (file: File, checkedItems: BillItemChecklistEntry[]) => void;
  onDelete: () => void;
  onPreview?: (url: string) => void;
};

const BillRequiredDocumentModal: React.FC<BillRequiredDocumentModalProps> = ({
  visible,
  documentName,
  itemNames,
  itemEntries,
  uploadedDocument,
  initialCheckedItems,
  hideItemChecklist = false,
  portalSubmission,
  isUploading,
  onCancel,
  onUpload,
  onDelete,
  onPreview,
}) => {
  const [checkedItems, setCheckedItems] = useState<BillItemChecklistEntry[]>(
    [],
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const isUploaded = Boolean(uploadedDocument?.documentUrl);

  const itemKey = (itemName: string, itemDate?: string) =>
    `${itemName}::${itemDate || ""}`;

  useEffect(() => {
    if (!visible) {
      setSelectedFile(null);
      return;
    }

    if (initialCheckedItems?.length) {
      setCheckedItems(initialCheckedItems);
    } else {
      const sourceItems: BillRequiredItem[] = itemEntries?.length
        ? itemEntries
        : (itemNames || []).map((itemName) => ({ itemName, itemDate: "" }));

      setCheckedItems(
        sourceItems.map((item) => {
          const saved = uploadedDocument?.itemNames?.find((entry) => {
            if (entry.itemName !== item.itemName) return false;
            if (item.itemDate && entry.itemDate) {
              return entry.itemDate === item.itemDate;
            }
            return true;
          });
          return {
            itemName: item.itemName,
            itemDate: item.itemDate || undefined,
            checked: saved?.checked ?? true,
          };
        }) || [],
      );
    }
    setSelectedFile(null);
  }, [visible, itemNames, itemEntries, uploadedDocument, initialCheckedItems]);

  const handleToggle = (
    itemName: string,
    checked: boolean,
    itemDate?: string,
  ) => {
    if (isUploaded) {
      const wasLocked = uploadedDocument?.itemNames?.some((entry) => {
        if (entry.itemName !== itemName || !entry.checked) return false;
        if (itemDate && entry.itemDate) return entry.itemDate === itemDate;
        return true;
      });
      if (wasLocked) return;
    }

    setCheckedItems((prev) =>
      prev.map((entry) =>
        itemKey(entry.itemName, entry.itemDate) === itemKey(itemName, itemDate)
          ? { ...entry, checked }
          : entry,
      ),
    );
  };

  const isItemLocked = (itemName: string, itemDate?: string): boolean =>
    Boolean(
      isUploaded &&
        uploadedDocument?.itemNames?.some((entry) => {
          if (entry.itemName !== itemName || !entry.checked) return false;
          if (itemDate && entry.itemDate) return entry.itemDate === itemDate;
          return true;
        }),
    );

  const hasSelectedItems = checkedItems?.some((entry) => entry.checked);

  const handleUploadClick = () => {
    if (isUploaded) return;
    if (!hasSelectedItems) {
      errorMessage("Please select at least one bill item before uploading");
      return;
    }
    if (!selectedFile) {
      errorMessage("Please select a file to upload");
      return;
    }
    onUpload(selectedFile, checkedItems);
  };

  const uploadedFileName =
    uploadedDocument?.documentUrl?.split("/")?.pop() ||
    uploadedDocument?.documentName;

  return (
    <Modal
      title="Bill Required Document"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={560}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Document Name</div>
        <div>{documentName}</div>
      </div>

      {!hideItemChecklist ? (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Bill Items <span style={{ color: "red" }}>*</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {checkedItems?.map((entry, index) => (
              <Checkbox
                key={`${entry.itemName}::${entry.itemDate || ""}::${index}`}
                checked={entry.checked}
                disabled={
                  portalSubmission ||
                  isItemLocked(entry.itemName, entry.itemDate) ||
                  isUploading
                }
                onChange={(e) =>
                  handleToggle(entry.itemName, e.target.checked, entry.itemDate)
                }
              >
                {entry.itemDate
                  ? `${entry.itemName} (${entry.itemDate})`
                  : entry.itemName}
              </Checkbox>
            ))}
          </div>
        </div>
      ) : null}

      {isUploaded ? (
        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            padding: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <div style={{ flex: 1, wordBreak: "break-all" }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Uploaded File</div>
            <div>{uploadedFileName}</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {onPreview && uploadedDocument?.documentUrl ? (
              <Button
                icon={<EyeOutlined />}
                onClick={() => onPreview(uploadedDocument.documentUrl)}
                disabled={portalSubmission}
              />
            ) : null}
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={onDelete}
              disabled={portalSubmission || isUploading}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : (
        <div
          style={{
            border: "1px dashed #d9d9d9",
            borderRadius: 8,
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <Upload
            showUploadList={false}
            beforeUpload={() => false}
            disabled={portalSubmission || isUploading}
            onChange={(info) => {
              const file = info?.file?.originFileObj ?? info?.file;
              if (file instanceof File) {
                setSelectedFile(file);
              }
            }}
          >
            <Button
              icon={<UploadOutlined />}
              disabled={portalSubmission || isUploading}
            >
              Select File
            </Button>
          </Upload>
          {selectedFile ? (
            <div style={{ fontSize: 13, color: "#555" }}>{selectedFile.name}</div>
          ) : null}
          <Button
            type="primary"
            onClick={handleUploadClick}
            loading={isUploading}
            disabled={
              portalSubmission || !selectedFile || !hasSelectedItems || isUploading
            }
            style={{ backgroundColor: "rgb(0, 76, 143)", alignSelf: "flex-start" }}
          >
            Upload
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default BillRequiredDocumentModal;
